import {
  type ColumnDef,
  EmptyState,
  InlineMessage,
  ListView,
  PageContent,
  SegmentedButtonGroup,
  type SortState,
} from '@olytic/foundation';
import { Copy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDocumentTitle } from '../documentTitle';
import { ACCOUNT_MERGE } from '../objects/accountMerge';
import { CONTACT_MERGE } from '../objects/contactMerge';
import { type MergeSpec, RecordMergeDialog } from '../objects/recordMerge';
import {
  ADMIN_ONLY_MESSAGE,
  type DuplicateList,
  type DuplicateObject,
  type DuplicatePair,
  listDuplicates,
  pairsOf,
} from './possibleDuplicatesApi';

/**
 * Setup → Possible Duplicates (MOTHER-179; Cameron's card on OLYAA-131): the accounts or
 * contacts that share a name (GET /api/merge/duplicates, MOTHER-177), one row per merge, each
 * pairing its group's oldest record (Keep) with one other (Merge Away). "Review and Merge…"
 * opens that object's merge dialog (recordMerge.tsx, as the lists and record pages do) over the
 * two, the Keep record first. Admins only, as the route and the merge are: anyone else sees the
 * Admin-only message, and the route is never called.
 */

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const day = (iso: string) => (iso ? DATE.format(new Date(iso)) : '');
const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');

const OBJECT_OPTIONS: { value: DuplicateObject; label: string }[] = [
  { value: 'accounts', label: 'Accounts' },
  { value: 'contacts', label: 'Contacts' },
];

const SPECS: Record<DuplicateObject, MergeSpec> = { accounts: ACCOUNT_MERGE, contacts: CONTACT_MERGE };

const KEEP: ColumnDef<DuplicatePair> = { key: 'keep', label: 'Keep', primary: true, sortable: true, get: (row) => row.keep.name, href: () => null };
const MERGE_AWAY: ColumnDef<DuplicatePair> = { key: 'mergeAway', label: 'Merge Away', sortable: true, get: (row) => row.mergeAway.name };
const CREATED: ColumnDef<DuplicatePair> = {
  key: 'created', label: 'Created', sortable: true, nowrap: true, get: (row) => row.mergeAway.createdAt, render: (row) => day(row.mergeAway.createdAt),
};

const COLUMNS: Record<DuplicateObject, ColumnDef<DuplicatePair>[]> = {
  accounts: [KEEP, MERGE_AWAY, CREATED],
  contacts: [
    KEEP,
    { key: 'keepAccount', label: 'Keep Account', sortable: true, hideOnMobile: true, get: (row) => row.keep.accountName },
    MERGE_AWAY,
    { key: 'mergeAwayAccount', label: 'Merge Away Account', sortable: true, hideOnMobile: true, get: (row) => row.mergeAway.accountName },
    CREATED,
  ],
};

type Load = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; list: DuplicateList };

interface PossibleDuplicatesPageProps {
  fetcher: Fetch;
  /** The signed-in person's role key: only 'admin' may see and merge duplicates. */
  role: string | undefined;
}

export function PossibleDuplicatesPage({ fetcher, role }: PossibleDuplicatesPageProps): JSX.Element {
  useDocumentTitle('Possible Duplicates');
  const admin = role === 'admin';
  const [object, setObject] = useState<DuplicateObject>('accounts');
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | undefined>(undefined);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [merging, setMerging] = useState<[string, string] | null>(null);

  useEffect(() => {
    if (!admin) return undefined;
    let live = true;
    setLoad({ state: 'loading' });
    listDuplicates(fetcher, object).then(
      (list) => live && setLoad({ state: 'ready', list }),
      (error: unknown) => live && setLoad({ state: 'error', message: errorText(error) }),
    );
    return () => {
      live = false;
    };
  }, [admin, fetcher, object, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  // Choosing the object shown already changes nothing, so it calls nothing.
  const choose = useCallback((next: DuplicateObject) => {
    if (next === object) return;
    setNotice(null);
    setSearch('');
    setSort(undefined);
    setMerging(null);
    setObject(next);
  }, [object]);

  const list = load.state === 'ready' ? load.list : null;
  // The rows shown are always the chosen object's: a late answer for the other is never listed.
  const pairs = useMemo(() => (list && list.object === object ? pairsOf(list) : []), [list, object]);

  if (!admin) {
    return (
      <PageContent as="section" width="fill">
        <ListView
          objectLabel="Possible Duplicates"
          objectIcon={<Copy />}
          columns={COLUMNS.accounts}
          rows={[]}
          getRowId={(row) => row.id}
          mode="client"
          pagination="none"
          error={ADMIN_ONLY_MESSAGE}
        />
      </PageContent>
    );
  }

  return (
    <PageContent as="section" width="fill">
      {notice ? <InlineMessage tone={notice.tone} role={notice.tone === 'error' ? 'alert' : 'status'} className="mb-3">{notice.text}</InlineMessage> : null}
      <ListView
        // Keyed by object, so each starts with its own columns.
        key={object}
        objectLabel="Possible Duplicates"
        objectIcon={<Copy />}
        columns={COLUMNS[object]}
        rows={pairs}
        getRowId={(row) => row.id}
        mode="client"
        // One merge at a time, from its row: nothing acts on a selection.
        selectable={false}
        search={search}
        onSearchChange={setSearch}
        {...(sort ? { sort } : {})}
        onSortChange={setSort}
        rowActions={[{ label: 'Review and Merge…', onSelect: (row) => setMerging([row.keep.id, row.mergeAway.id]) }]}
        headerActions={<SegmentedButtonGroup label="Object" options={OBJECT_OPTIONS} value={object} onChange={choose} />}
        // The note is a paragraph, so plain text.
        note={list?.truncated && list.object === object ? `Showing the first ${list.groups.length} groups of ${list.totalGroups}.` : undefined}
        pagination="pages"
        onRefresh={reload}
        loading={load.state === 'loading'}
        {...(load.state === 'error' ? { error: load.message } : {})}
        empty={
          <EmptyState
            icon={<Copy />}
            title={search.trim() ? `No possible duplicates match “${search.trim()}”` : 'No possible duplicates found'}
            description="Matched by name only. Records with the same name are listed here for you to check."
          />
        }
      />
      <RecordMergeDialog
        spec={SPECS[object]}
        fetcher={fetcher}
        ids={merging}
        onClose={() => setMerging(null)}
        onMerged={(_survivorId, name) => {
          setMerging(null);
          setNotice({ tone: 'success', text: `Merged into ${name}.` });
          reload();
        }}
        onFailed={(text) => {
          setMerging(null);
          setNotice({ tone: 'error', text });
        }}
      />
    </PageContent>
  );
}
